import { useState } from "react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import HostPortrait from "@/components/HostPortrait";
import SectionBg from "@/components/SectionBg";
import { scrollToId } from "@/lib/attribution";
import { MODULES, BIO, PAIN, TRANSFORMS, TAKEAWAYS, FAQS, HOST, WEBINAR, ASSETS } from "@/data/content";

export const AccordionItem = ({ num, title, body, open, onToggle, testId }) => (
  <div className="border-b border-white/10">
    <button data-testid={testId} onClick={onToggle} className="w-full flex items-center gap-5 py-6 text-left group">
      <span className="font-mono text-sm text-[#D9622B]">{num}</span>
      <span className="flex-1 font-black uppercase text-base sm:text-lg tracking-wide text-[#F0ECE4] group-hover:text-[#D9622B] transition-colors" style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}>{title}</span>
      <span className={`text-[#D9622B] text-xl transition-transform duration-300 ${open ? "rotate-45" : ""}`}>+</span>
    </button>
    <div className="grid transition-all duration-400" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
      <div className="overflow-hidden">
        <p className="pb-6 pl-10 pr-6 text-sm text-white/55 leading-relaxed max-w-2xl">{body}</p>
      </div>
    </div>
  </div>
);

export const Curriculum = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative px-6 sm:px-12 lg:px-16 py-28" data-testid="curriculum-section">
      <SectionBg src={ASSETS.bg.veteranFamily} opacity={0.13} position="center 40%" />
      <div className="relative z-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <SectionLabel num="004" text="THE CURRICULUM" />
          <Reveal><h2 className="mt-6 section-h">GET A SNEAK PEEK<br />INTO THE WEBINAR</h2></Reveal>
        </div>
        <p className="font-mono text-[11px] tracking-[0.2em] text-white/40">{WEBINAR.moduleCount} MODULES · {WEBINAR.duration} TOTAL</p>
      </div>
      <Reveal delay={0.1}>
        <div className="mt-12 border-t border-white/10">
          {MODULES.map((m, i) => (
            <AccordionItem key={m.num} {...m} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} testId={`module-accordion-${i + 1}`} />
          ))}
        </div>
      </Reveal>
      </div>
    </section>
  );
};

export const Host = () => (
  <section id="host" className="px-6 sm:px-12 lg:px-16 py-28" data-testid="host-section">
    <SectionLabel num="005" text="YOUR HOST" />
    <div className="mt-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
      <Reveal className="max-w-md w-full mx-auto lg:mx-0"><HostPortrait /></Reveal>
      <div>
        <Reveal><h2 className="section-h">GUIDANCE FROM SOMEONE WHO WORKS INSIDE THE PROCESS.</h2></Reveal>
        <Reveal delay={0.08}><div className="mt-6 h-px w-24 bg-[#D9622B]" /></Reveal>
        <div className="mt-8 space-y-5">
          {BIO.map((p, i) => (
            <Reveal key={i} delay={0.1 + i * 0.06}><p className="text-[15px] text-white/60 leading-[1.8]">{p}</p></Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10" data-testid="credential-grid">
            {[["COMPANY", HOST.company], ["NMLS#", HOST.nmls], ["CO. NMLS#", HOST.companyNmls], ["LICENSED IN", HOST.licensedIn]].map(([k, v]) => (
              <div key={k} className="bg-[#080808] p-5">
                <p className="font-mono text-[10px] tracking-[0.25em] text-white/30">{k}</p>
                <p className="mt-2 text-sm font-bold text-[#F0ECE4]">{v}</p>
              </div>
            ))}
          </div>
          <button data-testid="host-cta" onClick={() => scrollToId("register")} className="mt-8 px-8 py-4 btn-primary text-black font-black text-sm tracking-[0.1em]">
            ✓ RESERVE MY FREE SEAT
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Transform = () => (
  <section className="relative px-6 sm:px-12 lg:px-16 py-28" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="transform-section">
    <SectionBg src={ASSETS.bg.luxHomeDusk} opacity={0.12} position="center 75%" />
    <div className="relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-20">
      <div>
        <SectionLabel num="006" text="SOUND FAMILIAR?" />
        <Reveal><h2 className="mt-6 section-h">IF YOU'RE LIKE MOST VETERAN & ACTIVE-DUTY BUYERS I WORK WITH...</h2></Reveal>
        <div className="mt-8 space-y-5">
          {PAIN.map((p, i) => (
            <Reveal key={i} delay={0.08 + i * 0.06}><p className="text-[15px] text-white/60 leading-[1.8]">{p}</p></Reveal>
          ))}
        </div>
      </div>
      <div className="lg:pt-16">
        <Reveal>
          <p className="font-mono text-[12px] tracking-[0.25em] text-[#D9622B]">HERE'S WHAT CHANGES AFTER THIS WEBINAR:</p>
          <ul className="mt-8 space-y-6">
            {TRANSFORMS.map((t, i) => (
              <li key={t} data-testid={`transform-bullet-${i + 1}`} className="flex gap-4 text-[15px] text-[#F0ECE4] leading-relaxed">
                <span className="text-[#D9622B] font-bold shrink-0">→</span>{t}
              </li>
            ))}
          </ul>
          <button data-testid="transform-cta" onClick={() => scrollToId("register")} className="mt-10 px-8 py-4 btn-primary text-black font-black text-sm tracking-[0.1em]">
            ✓ RESERVE MY FREE SEAT
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Takeaways = () => (
  <section className="px-6 sm:px-12 lg:px-16 py-28" data-testid="takeaways-section">
    <SectionLabel num="007" text="THE TAKEAWAYS" />
    <Reveal><h2 className="mt-6 section-h">WHAT YOU'LL WALK AWAY WITH</h2></Reveal>
    <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
      {TAKEAWAYS.map((t, i) => (
        <Reveal key={t.title} delay={i * 0.06}>
          <div data-testid={`takeaway-card-${i + 1}`} className="lift-card h-full p-6 lg:p-8 border border-white/10 bg-white/[0.02] hover:border-[#D9622B]/50">
            <span className="text-3xl">{t.icon}</span>
            <h3 className="mt-4 font-black uppercase text-lg" style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}>{t.title}</h3>
            <p className="mt-2 text-[13px] text-white/45 leading-relaxed">{t.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative px-6 sm:px-12 lg:px-16 py-28" data-testid="faq-section">
      <SectionBg src={ASSETS.bg.luxHomeEstate} opacity={0.11} position="center 20%" />
      <div className="relative z-10">
      <SectionLabel num="008" text="BEFORE YOU ASK" />
      <Reveal><h2 className="mt-6 section-h">COMMON QUESTIONS</h2></Reveal>
      <Reveal delay={0.1}>
        <div className="mt-12 border-t border-white/10 max-w-4xl">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} num={String(i + 1).padStart(2, "0")} title={f.q} body={f.a} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} testId={`faq-accordion-${i + 1}`} />
          ))}
        </div>
      </Reveal>
      </div>
    </section>
  );
};
